import type { ApiClient } from '../../api/client.js';
import type { WifiNetwork } from '../../api/wifi-types.js';
import { networkLimitNote, networksLabel, wifiErrorText } from '../wifi-text.js';
import { h, type View } from './dom.js';
import { createNetworkPicker } from './wifi-network-picker.js';

export interface SavedNetworkChooser extends View {
  /** Reapply busy state, the send label and the one-network warning. */
  refresh(busy: boolean): void;
}

/** «Usar una de mis redes guardadas» inside a device row: loads the vault, sends ids and never sees a password. */
export function createSavedNetworkChooser(options: {
  api: Pick<ApiClient, 'listWifiNetworks'>;
  /** Networks the unit keeps, from its INFO; 1 when unknown. */
  limit(): number;
  onSend(ids: string[]): void;
  onBack(): void;
}): SavedNetworkChooser {
  const ctl = new AbortController();
  let networks: WifiNetwork[] = [];
  let loading = true;
  let busy = false;
  const status = h('p', { class: 'card-sub', role: 'status', 'aria-live': 'polite' }, ['Cargando tus redes guardadas…']);
  const retry = h('button', { class: 'btn ghost small', type: 'button', hidden: true }, ['Reintentar']);
  const empty = h('p', { class: 'card-sub', hidden: true }, ['Todavía no has guardado ninguna red. Guárdalas en ', h('a', { href: '#/redes' }, ['Redes Wi-Fi']), ' y vuelve aquí.']);
  const picker = createNetworkPicker({ legend: 'Redes guardadas que se enviarán', onChange: () => refresh(busy) });
  const note = h('p', { class: 'card-sub saved-chooser-note', hidden: true, 'aria-live': 'polite' });
  const send = h('button', { class: 'btn primary', type: 'button' }, ['Enviar redes']);
  const back = h('button', { class: 'btn ghost', type: 'button' }, ['Escribir una red']);
  const root = h('div', { class: 'form-stack saved-network-chooser' }, [
    h('p', { class: 'card-sub' }, ['Elige las redes de tu cuenta que quieres enviar. Las contraseñas no se muestran: viajan cifradas desde tu cuenta hasta el aparato.']),
    status, retry, empty, picker.el, note, h('div', { class: 'form-row' }, [send, back]),
  ]);

  function refresh(isBusy: boolean): void {
    busy = isBusy;
    const ids = picker.selected();
    const limit = options.limit();
    const sent = Math.min(ids.length, limit);
    send.textContent = sent > 0 ? `Enviar ${networksLabel(sent)}` : 'Enviar redes';
    send.disabled = busy || loading || sent === 0;
    back.disabled = busy;
    retry.disabled = busy;
    picker.setDisabled(busy || loading);
    picker.setLimit(limit);
    const text = networkLimitNote(ids.map(id => networks.find(network => network.id === id)?.ssid ?? ''), limit);
    note.textContent = text ?? ''; note.hidden = !text;
  }
  async function load(): Promise<void> {
    loading = true; retry.hidden = true; empty.hidden = true; status.textContent = 'Cargando tus redes guardadas…';
    refresh(busy);
    try {
      const result = await options.api.listWifiNetworks(ctl.signal);
      if (ctl.signal.aborted) return;
      networks = result.networks; loading = false;
      picker.setNetworks(networks);
      status.textContent = '';
      empty.hidden = networks.length > 0; picker.el.hidden = networks.length === 0;
    } catch (error) {
      if (ctl.signal.aborted) return;
      loading = false; status.textContent = wifiErrorText(error); retry.hidden = false; picker.el.hidden = true;
    }
    refresh(busy);
  }
  send.addEventListener('click', () => { const ids = picker.selected(); if (!busy && ids.length) options.onSend(ids); });
  back.addEventListener('click', () => { if (!busy) options.onBack(); });
  retry.addEventListener('click', () => { void load(); });
  void load();
  return { el: root, refresh, destroy() { ctl.abort(); picker.destroy?.(); } };
}
